import React, { useEffect, useMemo, useState } from 'react';
import { Header, Label, Loader, Message, Table } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';

import { API, showError } from '../../helpers';
import BillingDateCalendarV89, {
  serializeDateIntervalsV89,
} from '../../components/BillingDateCalendarV89';
import useBillingDateSelectionV97 from './BillingDateSelectionStateV97';

const formatTimeV89 = (timestamp) =>
  timestamp ? new Date(Number(timestamp) * 1000).toLocaleDateString() : '-';

const BillingDateSelectionV89 = ({ endpoint, billingDay = 1 }) => {
  const { t } = useTranslation();
  const {
    dates: selectedDates,
    setDates: setSelectedDates,
    refreshToday,
  } = useBillingDateSelectionV97(endpoint, billingDay);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const intervals = useMemo(
    () => serializeDateIntervalsV89(selectedDates),
    [selectedDates]
  );

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!intervals) {
        setSummary(null);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const response = await API.get(
          `${endpoint}?date_intervals=${encodeURIComponent(intervals)}`
        );
        if (!response?.data?.success) throw new Error(response?.data?.message);
        if (active) setSummary(response.data.data || null);
      } catch (error) {
        if (active) showError(error.message || t('billing.errors.load'));
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [endpoint, intervals, t]);

  return (
    <section className='billing-date-selection-v89'>
      <Header as='h3'>{t('billing.calendar.title')}</Header>
      <BillingDateCalendarV89
        billingDay={billingDay}
        value={selectedDates}
        onChange={setSelectedDates}
        onRefreshToday={refreshToday}
        eligibilityEndpoint={endpoint.replace(
          '/date-selection-summary',
          '/eligibility-timeline'
        )}
      />
      {loading ? (
        <Loader active inline='centered' />
      ) : !summary ? (
        <Message info content={t('billing.calendar.empty_selection')} />
      ) : (
        <>
          <div className='billing-selection-metrics-v89'>
            <div>
              <span>{t('billing.calendar.effective_cost')}</span>
              <strong>USD {summary.effective_amount_usd}</strong>
            </div>
            <div>
              <span>{t('billing.calendar.list_cost')}</span>
              <strong>USD {summary.list_amount_usd}</strong>
              <small>CNY {summary.list_amount_cny}</small>
            </div>
            <div>
              <span>{t('billing.calendar.discount')}</span>
              <strong>USD {summary.discount_amount_usd}</strong>
            </div>
            <div>
              <span>{t('billing.calendar.requests')}</span>
              <strong>
                {Number(summary.request_count || 0).toLocaleString()}
              </strong>
              {summary.unresolved_request_count ? (
                <small>
                  {t('billing.calendar.unresolved', {
                    count: summary.unresolved_request_count,
                  })}
                </small>
              ) : null}
            </div>
          </div>
          {summary.excluded_disabled_request_count ? (
            <Message
              info
              content={t('billing.calendar.excluded_disabled', {
                count: summary.excluded_disabled_request_count,
              })}
            />
          ) : null}
          {summary.excluded_unknown_request_count ? (
            <Message
              warning
              content={t('billing.calendar.excluded_unknown', {
                count: summary.excluded_unknown_request_count,
              })}
            />
          ) : null}
          {summary.periods?.length ? (
            <div
              className='management-table-scroll-v72'
              role='region'
              tabIndex={0}
            >
              <Table
                basic='very'
                compact
                unstackable
                className='management-table-v72'
              >
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>
                      {t('billing.statement.period')}
                    </Table.HeaderCell>
                    <Table.HeaderCell>
                      {t('billing.calendar.status')}
                    </Table.HeaderCell>
                    <Table.HeaderCell>
                      {t('billing.calendar.requests')}
                    </Table.HeaderCell>
                    <Table.HeaderCell>
                      {t('billing.calendar.list_cost')}
                    </Table.HeaderCell>
                    <Table.HeaderCell>
                      {t('billing.calendar.discount')}
                    </Table.HeaderCell>
                    <Table.HeaderCell>
                      {t('billing.calendar.effective_cost')}
                    </Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {summary.periods.map((period) => (
                    <Table.Row
                      key={`${period.period_start}:${period.period_end}`}
                    >
                      <Table.Cell>
                        {formatTimeV89(period.period_start)} -{' '}
                        {formatTimeV89(period.period_end)}
                      </Table.Cell>
                      <Table.Cell>
                        <Label
                          basic
                          color={
                            period.status === 'current'
                              ? 'blue'
                              : period.status === 'restated'
                              ? 'orange'
                              : 'grey'
                          }
                        >
                          {t(`billing.calendar.period_status.${period.status}`)}
                        </Label>
                        {period.revision_number > 0
                          ? ` v${period.revision_number}`
                          : ''}
                      </Table.Cell>
                      <Table.Cell>
                        {Number(period.request_count || 0).toLocaleString()}
                        {period.unresolved_request_count ? (
                          <small className='billing-cell-secondary-v8'>
                            {t('billing.calendar.unresolved', {
                              count: period.unresolved_request_count,
                            })}
                          </small>
                        ) : null}
                      </Table.Cell>
                      <Table.Cell>
                        USD {period.list_amount_usd}
                        <small className='billing-cell-secondary-v8'>
                          CNY {period.list_amount_cny}
                        </small>
                      </Table.Cell>
                      <Table.Cell>
                        USD {period.discount_amount_usd}
                        <small className='billing-cell-secondary-v8'>
                          {period.mixed_discount_v1022
                            ? t('setting.billing.immediate.segmented')
                            : `${(
                                Number(period.payable_basis_points || 0) / 100
                              ).toFixed(2)}%`}
                        </small>
                      </Table.Cell>
                      <Table.Cell>
                        <strong>USD {period.effective_amount_usd}</strong>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          ) : (
            <Message info content={t('billing.calendar.no_usage')} />
          )}
        </>
      )}
    </section>
  );
};

export default BillingDateSelectionV89;
